import { ApiResponse, typeMethod } from '@monorepo/models';
import { currentOrigin } from 'src/app-config';
import { httpHeaders } from '@monorepo/helpers';
import { localStorageHelper } from '../helpers/localStorageHelper';
import axios from 'axios';

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null && !Array.isArray(value);

class Api {
    private _fedErrorResponse: ApiResponse = {
        data: undefined,
        message: {
            description: 'No hubo comunicación con el sistema; ver si hay internet',
            title: 'Algo falló',
        },
        status: 500,
        success: false
    };
    private GetHeaders = (recaptchaToken: string | null = null, isFile = false): Record<string, string> => {
        const headers: Record<string, string> = {};
        headers[httpHeaders.accept] = httpHeaders.contentTypeApplicationJson;
        headers[httpHeaders.contentType] = isFile ? httpHeaders.contentTypeMultipartFormData : httpHeaders.contentTypeApplicationJson;
        if (recaptchaToken) {
            headers[httpHeaders.recaptchaToken] = recaptchaToken ?? '';
        }
        if (currentOrigin) {
            headers[httpHeaders.requestDomain] = currentOrigin;
        }
        const token = localStorageHelper.GetAuthToken();
        if (token) {
            headers[httpHeaders.authToken] = token;
        }
        return headers;
    }
    private UpdateAuthTokenFromBody = (body: unknown): void => {
        if (!isRecord(body) || !isRecord(body.data) || typeof body.data.newToken !== 'string') {
            return;
        }
        localStorageHelper.SetAuthToken(body.data.newToken);
    }
    SendRequest = async <T = undefined>(url: string, method: typeMethod, body: Record<string, any> | null = null, recaptchaToken: string | null = null): Promise<ApiResponse<T>> => {
        const options: RequestInit = {
            headers: this.GetHeaders(recaptchaToken),
            method
        };
        if (method !== 'GET' && body) {
            options.body = JSON.stringify(body)
        }
        try {
            const response = await fetch(url, options);
            const responseToken = response.headers.get(httpHeaders.authToken);
            if (responseToken) {
                localStorageHelper.SetAuthToken(responseToken);
            }
            const data = await response.json() as ApiResponse<T> | undefined;
            this.UpdateAuthTokenFromBody(data);
            return data ?? this._fedErrorResponse as ApiResponse<T>;
        } catch (error) {
            console.log(error);
            return this._fedErrorResponse as ApiResponse<T>;
        }
    }
    SendRequestDownloadFile = async <T = any>(url: string, openingNewTab?: boolean, fileName?: string, returnBlob?: boolean): Promise<ApiResponse<T>> => {
        try {
            const response = await fetch(url, {
                method: 'GET',
                headers: this.GetHeaders()
            });
            const responseToken = response.headers.get(httpHeaders.authToken);
            if (responseToken) {
                localStorageHelper.SetAuthToken(responseToken);
            }
            const responseClone = response.clone();
            const blob = await response.blob();
            const objectUrl = window.URL.createObjectURL(blob);
            const data: ApiResponse<any> = {
                message: {
                    description: '',
                    title: 'Logrado'
                },
                status: 200,
                success: true
            };
            if (returnBlob) {
                data.data = { fileObjectUrl: objectUrl };
                return data;
            }
            //
            if (openingNewTab) {
                window.open(objectUrl, '_blank');
            } else {
                const a = document.createElement('a');
                a.href = objectUrl;
                a.download = fileName ?? 'archivo.pdf';
                document.body.appendChild(a);
                a.click();
                a.remove();
            }
            window.URL.revokeObjectURL(objectUrl);
            try {
                const dataJson = await responseClone.json() as ApiResponse<T> | undefined;
                if (dataJson) {  // it's a controlled error response
                    this.UpdateAuthTokenFromBody(dataJson);
                    return dataJson;
                }
            } catch (error) {
                console.error('Error parsing JSON response:', error);
            }
            return data;
        } catch (error) {
            console.log(error);
            return this._fedErrorResponse as ApiResponse<T>;
        }
    }
    SendRequestSendFile = async (url: string, file: File): Promise<ApiResponse> => {
        try {
            const formData = new FormData();
            formData.append('pdf', file);
            const options = {
                headers: this.GetHeaders(null, true),
            };
            const response = await axios.post(url, formData, options);
            const data = response.data as ApiResponse | undefined;
            const responseToken = response.headers[httpHeaders.authToken];
            if (typeof responseToken === 'string') {
                localStorageHelper.SetAuthToken(responseToken);
            }
            this.UpdateAuthTokenFromBody(data);
            return data ?? this._fedErrorResponse;
        } catch (error) {
            console.log(error);
            return this._fedErrorResponse;
        }
    }
}

export const api = new Api();
